import { cn } from '@/lib/utils';
import type { CompareState, CompareSlot } from '@/types/reports';
import type { Group, Company, IndustryType } from '@/types';
import { CompareSlotCard } from './CompareSlotCard';
import { Plus, GitCompare } from 'lucide-react';

// Helper to convert hex color to rgba with alpha
function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(0, 0, 0, ${alpha})`;
}

interface CompareAnalyticsPanelProps {
  compareState: CompareState;
  onCompareStateChange: (state: CompareState) => void;
  onAddSlot: () => void;
  onRemoveSlot: (slotId: string) => void;
  groups: Group[];
  companies: Company[];
  selectedIndustryTypes: IndustryType[];
  groupLabel: string;
  // New props for header
  dateRange?: { start: Date | null; end: Date | null };
  selectedCompany?: Company | null;
}

export function CompareAnalyticsPanel({
  compareState,
  onCompareStateChange,
  onAddSlot,
  onRemoveSlot,
  groups,
  companies,
  selectedIndustryTypes,
  groupLabel,
  dateRange: _dateRange,
  selectedCompany,
}: CompareAnalyticsPanelProps) {
  const { slots, activeSections } = compareState;

  // Update a specific slot
  const handleSlotChange = (slotId: string, updates: Partial<CompareSlot>) => {
    const newSlots = slots.map(slot =>
      slot.id === slotId ? { ...slot, ...updates } : slot
    );
    onCompareStateChange({
      ...compareState,
      slots: newSlots,
    });
  };

  return (
    <div className="min-w-0 h-full flex flex-col">
      {/* Compare Header - similar to GroupAnalyticsPanel but without date (shown per slot) */}
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          {/* Title: "Compare" + groupLabel */}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-semibold flex items-start gap-2">
              <GitCompare className="h-5 w-5 text-[#8FB560] shrink-0 mt-0.5" />
              <span>Compare {groupLabel}s</span>
            </h2>
          </div>

          {/* View label + Company Logo - right side */}
          <div className="shrink-0 hidden md:flex items-center gap-8">
            <span className="text-lg font-semibold tracking-[0.28em] text-white">COMPARE</span>
            <img
              src={selectedCompany?.logo_url || '/assets/ht-logo.png'}
              alt={selectedCompany?.name || 'Hollywood Trucks'}
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      </div>

      {slots.length === 0 ? (
          /* Empty State - No slots yet, stretches to match sidebar height */
          <div className="bg-card border border-border rounded-lg p-8 flex flex-col items-center justify-center flex-1 min-h-[400px]">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-medium">Compare {groupLabel}s</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  Select {groupLabel.toLowerCase()}s from the sidebar to compare their analytics side-by-side.
                  You can compare up to 4 items at once.
                </p>
              </div>
            </div>
          </div>
        ) : activeSections.includes('chart') && activeSections.length === 1 ? (
          /* Chart Section Only: Horizontal 2x2 grid for side-by-side comparison */
          <div className={cn(
            "grid gap-3",
            slots.length === 1 && "grid-cols-1",
            slots.length === 2 && "grid-cols-2",
            slots.length >= 3 && "grid-cols-2",
          )}>
            {slots.map((slot, index) => (
              <CompareSlotCard
                key={slot.id}
                slot={slot}
                index={index}
                activeSections={activeSections}
                onRemove={() => onRemoveSlot(slot.id)}
                onSlotChange={(updates) => handleSlotChange(slot.id, updates)}
                groups={groups}
                companies={companies}
                selectedIndustryTypes={selectedIndustryTypes}
                groupLabel={groupLabel}
                compareState={compareState}
                // Third slot spans full width when 3 total
                className={cn(
                  index === 2 && slots.length === 3 && "col-span-2"
                )}
              />
            ))}
            {/* Add Slot Placeholder - only show if less than 4 slots */}
            {slots.length < 4 && (
              <button
                onClick={onAddSlot}
                className={cn(
                  "border-2 border-dashed border-border rounded-lg p-8 flex flex-col items-center justify-center",
                  "hover:border-[#8FB560]/50 hover:bg-muted/20 transition-colors min-h-[200px]",
                  "text-muted-foreground hover:text-foreground",
                  slots.length === 3 && "col-span-2"
                )}
              >
                <Plus className="h-8 w-8 mb-2" />
                <span className="text-sm font-medium">Add {groupLabel}</span>
              </button>
            )}
          </div>
        ) : (
          /* Multiple Sections: Stacked vertical sections with side-by-side columns per section */
          <div className="space-y-6">
            {activeSections.map((section) => {
              return (
                <div key={section} className="bg-card border border-border rounded-lg overflow-hidden">
                  {/* Section Title Row with Slot Color Indicators */}
                  <div className="border-b border-border bg-muted/10">
                    {/* Section title */}
                    <div className="px-4 py-2">
                      <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                        {section === 'info' && 'Comparison Info'}
                        {section === 'kpis' && 'Key Performance Indicators'}
                        {section === 'chart' && 'Time Series Chart'}
                        {section === 'stats' && 'Statistics Summary'}
                        {section === 'energyMix' && 'Energy Mix Breakdown'}
                        {section === 'gridRevenue' && 'Grid Revenue & Savings'}
                      </h3>
                    </div>

                    {/* Slot color indicator bar - shows slot labels with their colors */}
                    <div className={cn(
                      "grid border-t border-border/50",
                      slots.length === 1 && "grid-cols-1",
                      slots.length === 2 && "grid-cols-2",
                      slots.length === 3 && "grid-cols-3",
                      slots.length === 4 && "grid-cols-2 lg:grid-cols-4",
                    )}>
                      {slots.map((slot, index) => {
                        // Get slot label
                        const slotLabel = slot.label || (slot.groupIds.length === 0
                          ? `Slot ${index + 1}`
                          : groups.find(g => g.id === slot.groupIds[0])?.name || `Slot ${index + 1}`);

                        return (
                          <div
                            key={slot.id}
                            className={cn(
                              "px-3 py-1.5 flex items-center gap-2",
                              index > 0 && "border-l border-border/50"
                            )}
                            style={{
                              backgroundColor: hexToRgba(slot.color, 0.15),
                              borderBottom: `3px solid ${slot.color}`,
                            }}
                          >
                            <div
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: slot.color }}
                            />
                            <span className="text-xs font-medium truncate" style={{ color: slot.color }}>
                              {slotLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Content Row - each slot's data in columns (dynamic based on slot count) */}
                  <div className={cn(
                    "grid",
                    slots.length === 1 && "grid-cols-1",
                    slots.length === 2 && "grid-cols-2",
                    slots.length === 3 && "grid-cols-3",
                    slots.length === 4 && "grid-cols-2 lg:grid-cols-4",
                  )}>
                    {slots.map((slot, index) => {
                      // Calculate border classes based on slot count and position
                      // Add left border to all columns except the first
                      let borderClass = "";
                      if (index > 0) {
                        if (slots.length === 4) {
                          // For 4 slots: responsive layout (2x2 mobile, 1x4 desktop)
                          // Mobile: border on odd indices (1,3) which are right columns in 2x2
                          // Desktop: border on all except first (1,2,3)
                          borderClass = index % 2 === 1
                            ? "border-l border-border"
                            : "lg:border-l lg:border-border";
                        } else {
                          // For 2 or 3 slots: simple left border on all except first
                          borderClass = "border-l border-border";
                        }
                      }

                      // Use slot color as subtle background tint for better visual grouping
                      // Alternate intensity for additional differentiation
                      const bgAlpha = index % 2 === 0 ? 0.03 : 0.08;

                      return (
                        <div
                          key={slot.id}
                          className={cn(
                            "p-3",
                            borderClass
                          )}
                          style={{
                            backgroundColor: hexToRgba(slot.color, bgAlpha),
                          }}
                        >
                          <CompareSlotCard
                            slot={slot}
                            index={index}
                            activeSections={activeSections}
                            activeSection={section}
                            onRemove={() => onRemoveSlot(slot.id)}
                            onSlotChange={(updates) => handleSlotChange(slot.id, updates)}
                            groups={groups}
                            companies={companies}
                            selectedIndustryTypes={selectedIndustryTypes}
                            groupLabel={groupLabel}
                            compareState={compareState}
                            compactMode
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
